// fx.js — パーティクル・画面揺れ・フラッシュなどの演出レイヤ

const TAU = Math.PI * 2;
const rand = (a, b) => a + Math.random() * (b - a);

export class FX {
  constructor() {
    this.parts = [];
    this.texts = [];
    this.rings = [];
    this.rays = 0;        // 放射光の強さ 0..1
    this.rayAngle = 0;
    this.shake = 0;
    this.flash = 0;
    this.flashColor = '#fff';
    this.chroma = 0;      // 色収差の強さ
    this.time = 0;
  }

  reset() {
    this.parts.length = 0;
    this.texts.length = 0;
    this.rings.length = 0;
    this.rays = 0; this.shake = 0; this.flash = 0; this.chroma = 0;
  }

  // --- 生成 ----------------------------------------------------------

  spark(x, y, n, color, speed = 3, life = 22) {
    for (let i = 0; i < n; i++) {
      const a = rand(0, TAU), s = rand(0.3, 1) * speed;
      this.parts.push({
        x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 0.6,
        life, max: life, color, size: rand(1.2, 2.8), grav: 0.06, kind: 'spark',
      });
    }
  }

  burst(x, y, n, colors, speed = 6, life = 46) {
    for (let i = 0; i < n; i++) {
      const a = rand(0, TAU), s = rand(0.25, 1) * speed;
      this.parts.push({
        x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
        life, max: life, color: colors[(Math.random() * colors.length) | 0],
        size: rand(1.6, 4.2), grav: 0.09, kind: 'spark',
      });
    }
  }

  confetti(w, n, colors) {
    for (let i = 0; i < n; i++) {
      this.parts.push({
        x: rand(0, w), y: rand(-160, -10),
        vx: rand(-1.1, 1.1), vy: rand(1.6, 4.2),
        life: 300, max: 300, color: colors[(Math.random() * colors.length) | 0],
        size: rand(3, 7), grav: 0.012, kind: 'confetti',
        rot: rand(0, TAU), vr: rand(-0.22, 0.22), flip: rand(0.05, 0.16),
      });
    }
  }

  ring(x, y, color, maxR = 46, life = 26, width = 3) {
    this.rings.push({ x, y, r: 3, maxR, life, max: life, color, width });
  }

  text(x, y, str, color, size = 22, life = 60, vy = -1.1) {
    this.texts.push({ x, y, str, color, size, life, max: life, vy });
  }

  addShake(v) { this.shake = Math.min(26, this.shake + v); }
  addFlash(v, color = '#ffffff') { this.flash = Math.min(1, this.flash + v); this.flashColor = color; }

  // --- 更新 ----------------------------------------------------------

  update() {
    this.time++;
    this.rayAngle += 0.006 + this.rays * 0.02;
    this.shake *= 0.86;
    if (this.shake < 0.05) this.shake = 0;
    this.flash *= 0.88;
    if (this.flash < 0.004) this.flash = 0;
    this.chroma *= 0.92;

    for (let i = this.parts.length - 1; i >= 0; i--) {
      const p = this.parts[i];
      p.x += p.vx; p.y += p.vy; p.vy += p.grav;
      if (p.kind === 'confetti') { p.rot += p.vr; p.vx *= 0.995; }
      else { p.vx *= 0.965; p.vy *= 0.965; }
      if (--p.life <= 0 || p.y > 1200) this.parts.splice(i, 1);
    }
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i];
      const k = 1 - r.life / r.max;
      r.r = 3 + (r.maxR - 3) * (1 - Math.pow(1 - k, 2.2));
      if (--r.life <= 0) this.rings.splice(i, 1);
    }
    for (let i = this.texts.length - 1; i >= 0; i--) {
      const t = this.texts[i];
      t.y += t.vy; t.vy *= 0.97;
      if (--t.life <= 0) this.texts.splice(i, 1);
    }
  }

  shakeOffset() {
    if (!this.shake) return [0, 0];
    return [rand(-this.shake, this.shake), rand(-this.shake, this.shake)];
  }

  // --- 描画 ----------------------------------------------------------

  // 発光する要素は glow キャンバスへ描き、後段でブラーして加算合成する
  drawParticles(ctx, glow) {
    for (const p of this.parts) {
      const a = Math.min(1, p.life / (p.max * 0.55));
      if (p.kind === 'confetti') {
        const sq = Math.abs(Math.cos(p.rot));
        ctx.save();
        ctx.globalAlpha = a;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot * 0.4);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size * sq, p.size, p.size * 2 * sq);
        ctx.restore();
      } else {
        ctx.globalAlpha = a;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, TAU);
        ctx.fill();
        if (glow) {
          glow.globalAlpha = a * 0.9;
          glow.fillStyle = p.color;
          glow.beginPath();
          glow.arc(p.x / 2, p.y / 2, p.size * 0.9, 0, TAU);
          glow.fill();
        }
      }
    }
    ctx.globalAlpha = 1;
    if (glow) glow.globalAlpha = 1;
  }

  drawRings(ctx, glow) {
    for (const r of this.rings) {
      const a = r.life / r.max;
      ctx.globalAlpha = a * 0.9;
      ctx.strokeStyle = r.color;
      ctx.lineWidth = r.width * a + 0.5;
      ctx.beginPath();
      ctx.arc(r.x, r.y, r.r, 0, TAU);
      ctx.stroke();
      if (glow) {
        glow.globalAlpha = a * 0.8;
        glow.strokeStyle = r.color;
        glow.lineWidth = (r.width * a + 1) / 2;
        glow.beginPath();
        glow.arc(r.x / 2, r.y / 2, r.r / 2, 0, TAU);
        glow.stroke();
      }
    }
    ctx.globalAlpha = 1;
    if (glow) glow.globalAlpha = 1;
  }

  drawTexts(ctx) {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const t of this.texts) {
      const k = t.life / t.max;
      const pop = t.life > t.max - 6 ? 1 + (t.max - t.life) * 0.06 : 1;
      ctx.save();
      ctx.globalAlpha = Math.min(1, k * 1.7);
      ctx.translate(t.x, t.y);
      ctx.scale(pop, pop);
      ctx.font = `900 ${t.size}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(0,0,0,0.75)';
      ctx.strokeText(t.str, 0, 0);
      ctx.fillStyle = t.color;
      ctx.fillText(t.str, 0, 0);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  // 大当り時の回転放射光
  drawRays(ctx, cx, cy, R) {
    if (this.rays <= 0.01) return;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = this.rays * 0.30;
    ctx.translate(cx, cy);
    ctx.rotate(this.rayAngle);
    const n = 16;
    for (let i = 0; i < n; i++) {
      const a0 = (i / n) * TAU, a1 = a0 + TAU / (n * 2);
      const g = ctx.createLinearGradient(0, 0, Math.cos(a0) * R, Math.sin(a0) * R);
      g.addColorStop(0, 'rgba(255,240,150,0.9)');
      g.addColorStop(1, 'rgba(255,120,220,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, R, a0, a1);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  drawFlash(ctx, w, h) {
    if (this.flash <= 0.004) return;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = this.flash;
    ctx.fillStyle = this.flashColor;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }
}
